import { useEffect, useRef } from "react";

const TRAIL_COUNT = 16;

/**
 * Soft "AURA" cursor: a gradient orb that follows the mouse with a glowing halo
 * and a trail of feminine pastel sparkles fading behind.
 * Disabled on touch / reduced-motion devices.
 */
export const CursorGlow = () => {
  const haloRef = useRef(null);
  const orbRef = useRef(null);
  const trailRefs = useRef([]);

  useEffect(() => {
    const halo = haloRef.current;
    const orb = orbRef.current;
    if (!halo || !orb) return;

    if (typeof window !== "undefined" && window.matchMedia("(hover: none)").matches) {
      halo.style.display = "none";
      orb.style.display = "none";
      trailRefs.current.forEach((el) => el && (el.style.display = "none"));
      return;
    }

    let mouseX = window.innerWidth / 2;
    let mouseY = window.innerHeight / 2;
    let haloX = mouseX;
    let haloY = mouseY;
    let orbX = mouseX;
    let orbY = mouseY;
    let visible = false;
    let trailIdx = 0;
    let lastEmit = 0;

    const setVisible = (v) => {
      if (visible === v) return;
      visible = v;
      halo.style.opacity = v ? "0.55" : "0";
      orb.style.opacity = v ? "1" : "0";
    };

    const emitTrail = (x, y) => {
      const now = performance.now();
      if (now - lastEmit < 38) return;
      lastEmit = now;
      const el = trailRefs.current[trailIdx % TRAIL_COUNT];
      trailIdx++;
      if (!el) return;
      const offX = (Math.random() - 0.5) * 18;
      const offY = (Math.random() - 0.5) * 18;
      el.style.setProperty("--sx", `${x + offX}px`);
      el.style.setProperty("--sy", `${y + offY}px`);
      // restart animation
      el.classList.remove("sparkle-anim");
      // force reflow to retrigger anim
      // eslint-disable-next-line no-unused-expressions
      el.offsetWidth;
      el.classList.add("sparkle-anim");
    };

    const onMove = (e) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
      setVisible(true);
      emitTrail(mouseX, mouseY);
    };

    const onLeave = () => setVisible(false);

    const interactive = "a, button, [role='button'], input, textarea, select, [data-testid$='-button'], [data-testid^='nav-'], [data-testid$='-cta']";
    const onOver = (e) => {
      if (e.target.closest && e.target.closest(interactive)) {
        orb.classList.add("aura-orb-active");
        halo.style.opacity = "0.85";
      }
    };
    const onOut = (e) => {
      if (e.target.closest && e.target.closest(interactive)) {
        orb.classList.remove("aura-orb-active");
        halo.style.opacity = visible ? "0.55" : "0";
      }
    };

    window.addEventListener("mousemove", onMove, { passive: true });
    document.addEventListener("mouseleave", onLeave);
    document.addEventListener("mouseover", onOver, { passive: true });
    document.addEventListener("mouseout", onOut, { passive: true });

    let raf;
    const tick = () => {
      haloX += (mouseX - haloX) * 0.14;
      haloY += (mouseY - haloY) * 0.14;
      orbX += (mouseX - orbX) * 0.35;
      orbY += (mouseY - orbY) * 0.35;
      halo.style.transform = `translate3d(${haloX}px, ${haloY}px, 0) translate(-50%, -50%)`;
      orb.style.transform = `translate3d(${orbX}px, ${orbY}px, 0) translate(-50%, -50%)`;
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      window.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseleave", onLeave);
      document.removeEventListener("mouseover", onOver);
      document.removeEventListener("mouseout", onOut);
      cancelAnimationFrame(raf);
    };
  }, []);

  // Sparkle colours rotating through AURA palette
  const palette = ["#F9A8D4", "#C4B5FD", "#A78BFA", "#67E8F9", "#FBCFE8"];

  return (
    <>
      {/* Soft glowing halo */}
      <div
        ref={haloRef}
        aria-hidden
        className="pointer-events-none fixed top-0 left-0 z-[100] h-80 w-80 rounded-full blur-3xl opacity-0 transition-opacity duration-300"
        style={{
          background:
            "radial-gradient(circle, rgba(167,139,250,0.55) 0%, rgba(249,168,212,0.35) 40%, rgba(103,232,249,0.25) 65%, transparent 75%)",
          mixBlendMode: "multiply",
          willChange: "transform, opacity",
        }}
      />

      {/* Sparkle trail particles */}
      {Array.from({ length: TRAIL_COUNT }).map((_, i) => (
        <span
          // eslint-disable-next-line react/no-array-index-key
          key={i}
          ref={(el) => (trailRefs.current[i] = el)}
          aria-hidden
          className="aura-sparkle pointer-events-none fixed top-0 left-0 z-[100]"
          style={{ "--sx": "0px", "--sy": "0px", "--sparkle-color": palette[i % palette.length] }}
        />
      ))}

      {/* Main gradient orb (cursor) */}
      <div
        ref={orbRef}
        aria-hidden
        className="aura-orb pointer-events-none fixed top-0 left-0 z-[101] opacity-0 transition-opacity duration-200"
        style={{ willChange: "transform, opacity" }}
      />
    </>
  );
};
